import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Pencil, RotateCw, Save } from 'lucide-react';
import type { AddressColumnInfo } from '@uniticket/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { fetchAddressColumn, updateAddressColumn } from '@/hooks/useAddressColumnEdit';

// Modifier N'IMPORTE QUELLE colonne d'une adresse depuis la console DMP (demande du user, 17-09 : « ce sont les admins
// qui ont accès, on doit pouvoir tout modifier »). La saisie suit le type que le SERVEUR lit dans la base : un enum
// devient une liste, un booléen un vrai/faux, une colonne qui accepte NULL offre « Vider ». Postgres convertit et
// valide la valeur ; ce qu'il refuse revient tel quel.
//
// Rendue par la PAGE (hors de la GlassCard du panneau) et portalisée, pointer-events-auto + z-[60] (défaut B-3).

const NUMBER_TYPES = new Set(['int2', 'int4', 'int8', 'numeric', 'float4', 'float8']);

export function EditAddressColumnModal({
  jvid, column, addressLabel, onClose, onSaved, onOpenIdentity,
}: {
  jvid: number;
  column: string;
  addressLabel: string;
  onClose: () => void;
  onSaved: (jvid: number) => void;
  /** Les colonnes d'identité se corrigent par « Modifier l'adresse » : la page ouvre ce formulaire. */
  onOpenIdentity: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const k = (key: string, o?: Record<string, unknown>) => t(`tickets:dmpLookup.columnEdit.${key}`, o);
  const confirmDmpWrite = useDmpWriteConfirm();

  const [info, setInfo] = useState<AddressColumnInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [isNull, setIsNull] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setInfo(null);
    setLoadError(null);
    setError(null);
    try {
      const res = await fetchAddressColumn(jvid, column);
      setInfo(res.data);
      setDraft(res.data.valueText ?? '');
      setIsNull(res.data.valueText == null);
    } catch (err: unknown) {
      setLoadError((err as Error)?.message || t('tickets:dmpLookup.columnEdit.loadError'));
    }
  }, [jvid, column, t]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const newValue = isNull ? null : draft;
  const unchanged = info != null && newValue === info.valueText;
  const canSubmit = info?.editable === 'yes' && !unchanged && reason.trim().length >= 3 && !saving;

  const submit = async () => {
    if (!info || !canSubmit) return;
    if (!confirmDmpWrite(k('confirmWhat', { column }))) return;
    setSaving(true);
    setError(null);
    try {
      await updateAddressColumn(jvid, column, { value: newValue, expected: info.valueText, reason: reason.trim() });
      toast.success(k('saved', { column }));
      onSaved(jvid);
    } catch (err: unknown) {
      setError((err as Error)?.message || k('error'));
    } finally {
      setSaving(false);
    }
  };

  // La saisie, selon le type lu dans la base.
  const renderInput = (c: AddressColumnInfo) => {
    const options = c.enumValues ?? (c.udtName === 'bool' ? ['true', 'false'] : null);
    if (options) {
      const current = isNull ? '__null__' : draft;
      return (
        <select
          value={current}
          onChange={(e) => {
            if (e.target.value === '__null__') { setIsNull(true); return; }
            setIsNull(false);
            setDraft(e.target.value);
          }}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm"
        >
          {!options.includes(current) && current !== '__null__' && <option value={current}>{current}</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          {c.nullable && <option value="__null__">{k('nullOption')}</option>}
        </select>
      );
    }
    const placeholder = c.udtName === 'date' ? k('placeholderDate')
      : c.udtName.startsWith('timestamp') ? k('placeholderTimestamp')
        : NUMBER_TYPES.has(c.udtName) ? k('placeholderNumber') : undefined;
    return (
      <>
        <Input
          value={isNull ? '' : draft}
          disabled={isNull}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={isNull ? 'NULL' : placeholder}
          inputMode={NUMBER_TYPES.has(c.udtName) ? 'decimal' : undefined}
          maxLength={c.maxLength ?? undefined}
          className="font-mono"
        />
        {c.nullable && (
          <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={isNull} onChange={(e) => setIsNull(e.target.checked)} />
            {k('setNull')}
          </label>
        )}
      </>
    );
  };

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={k('title', { column })}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Pencil className="h-4 w-4 text-brand-rose" />
            {k('title', { column })}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">JVID {jvid} · {addressLabel}</p>
        </div>

        <div className="space-y-3 px-5 py-4">
          {!info && !loadError && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {k('loading')}
            </p>
          )}
          {loadError && <p className="text-xs font-medium text-red-600" role="alert">{loadError}</p>}

          {info && (
            <>
              <p className="text-[11px] text-slate-500">
                {k('type', { type: info.udtName })}
                {' · '}
                {info.nullable ? k('nullable') : k('notNull')}
                {info.maxLength != null && <>{' · '}{k('maxLength', { n: info.maxLength })}</>}
              </p>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{k('current')}</p>
                <p className={cn('break-all font-mono text-sm', info.valueText == null ? 'text-slate-300' : 'text-slate-800')}>
                  {info.valueText ?? '∅ (NULL)'}
                </p>
              </div>

              {info.editable === 'no' && (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  {info.refusal === 'type' ? k('typeRefused', { type: info.udtName }) : k('never')}
                </p>
              )}

              {info.editable === 'identity' && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs text-slate-600">{k('identity')}</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={onOpenIdentity}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    {k('openIdentity')}
                  </Button>
                </div>
              )}

              {info.editable === 'yes' && (
                <>
                  {info.warning && (
                    <p className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="alert">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {info.warning === 'geo' ? k('warnGeo') : k('warnDedicatedAction')}
                    </p>
                  )}
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{k('newValue')}</p>
                    {renderInput(info)}
                    {unchanged && <p className="mt-1 text-[11px] text-slate-400">{k('unchanged')}</p>}
                  </div>
                  <div>
                    <label htmlFor="column-edit-reason" className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {k('reason')}
                    </label>
                    <textarea
                      id="column-edit-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder={k('reasonPlaceholder')}
                      className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                    />
                  </div>
                </>
              )}
            </>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2" role="alert">
              <p className="text-xs font-medium text-red-700">{error}</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => void load()} disabled={saving}>
                <RotateCw className="mr-1.5 h-3.5 w-3.5" />
                {k('reload')}
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>{k('cancel')}</Button>
          {info?.editable === 'yes' && (
            <Button size="sm" onClick={() => void submit()} disabled={!canSubmit}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              {k('submit')}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
